import { beginMutation, endMutation } from "./pendingMutations";

/**
 * `fetch`, with the app-wide busy overlay attached.
 *
 * Identical to `fetch` in every observable way — same arguments, same Response,
 * same errors — so it is a drop-in at a call site that needs the raw Response
 * (a status check, a stream, a blob). The only addition is that a non-GET
 * request is counted in pendingMutations while it is in flight, which is what
 * NavigationProgress renders the spinner from.
 *
 * Why this exists alongside apiFetch: apiFetch throws on a non-2xx and returns
 * parsed JSON, which is the better contract and where new code should go. But
 * the ~100 client components that predate it each check `res.ok` and build their
 * own error message, and rewriting all of them to apiFetch's contract would
 * change control flow and error text at every one. This gets them the overlay
 * with a one-identifier change and no behavior change; migrating a call site to
 * apiFetch properly is then an independent cleanup.
 *
 * The count releases when the response headers arrive, not when the body is
 * consumed — so a streaming response (console chat, prompt builder) shows the
 * overlay for the round trip and not for the whole stream.
 */
export function trackedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const method = (init?.method ?? "GET").toUpperCase();
  if (method === "GET") return fetch(input, init);

  beginMutation();
  // No try/finally: fetch() returns a promise rather than throwing synchronously,
  // and finally() covers both settle paths.
  return fetch(input, init).finally(endMutation);
}
